import { Button, Dropdown } from 'antd';
import { ReactNode, useMemo } from 'react';
import {
  ApiErrorResponse,
  confirmationHeaders,
  DropdownItem,
  GetApiErrorMessage,
  GroupDescriptionType,
  InputDataCopy,
  InputDataDetailsType,
  InputUpdateResponseType,
  PermissionCode,
  useEditSessionContext,
  useHasPermissionForDescription,
  useHasPermissionForGroup,
  useInputDataDetailsActionMutation,
} from '@atlas-frontend/sdk';
import { InputDataVersionType } from './types';
import { useSubmittedVersion } from './useSubmittedVersion';
import { useInputDataActions } from './useInputDataActions';
import { confirmInputDataWarnings } from './confirmInputDataWarnings';
import { useFormState } from 'react-hook-form';
import { ItemType } from 'antd/es/menu/interface';
import { MoreVertical } from 'react-feather';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataActionsEditDropdown.translations.json';
import {
  DirtyConfirmation,
  DirtyConfirmationProps,
  InputActionFactory,
} from './DirtyConfirmation';

const { addTranslations, t } = i18nNsFactory(
  'input-data-actions-edit-dropdown',
);
addTranslations(translations);
const translateErrorCode: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

interface InputDataActionsDropdownProps {
  group?: string;
  data?: InputDataDetailsType;
  disabled: boolean;
  description?: GroupDescriptionType;
  version?: InputDataVersionType;
  isLoading: boolean;
}

function createOption(
  props: DirtyConfirmationProps,
  needsConfirmation: boolean,
  disabled: boolean,
) {
  let label: ReactNode | string;
  let onClick: (() => void) | undefined = undefined;
  if (needsConfirmation) {
    label = <DirtyConfirmation {...props} />;
  } else {
    label = (
      <DropdownItem>
        {t(`button.${props.actionType}-${props.sampleType}`)}
      </DropdownItem>
    );
    onClick = props.action(props.actionType === 'copy');
  }
  return {
    key: `${props.actionType}-${props.sampleType}`,
    disabled,
    label,
    onClick,
  };
}

const confirmInputDataActionWarnings = (
  apiErrorResponse: ApiErrorResponse,
  makeAction: InputActionFactory,
  copy: boolean,
  onCancel: () => void,
) =>
  confirmInputDataWarnings(apiErrorResponse, translateErrorCode, {
    title: t(`confirm.title-${copy ? 'copy' : 'impute'}`),
    okText: t('confirm.okText'),
    cancelText: t('confirm.cancelText'),
    onOk: makeAction(copy, true),
    onCancel,
  });

export function InputDataActionsEditDropdown({
  data,
  disabled,
  description,
  version,
}: InputDataActionsDropdownProps) {
  const { mutateAsync } = useInputDataDetailsActionMutation();
  const selectedVersion = useSubmittedVersion(description, version);
  const { onClear, onError, onSuccess, onLoad, isLoading } =
    useInputDataActions();
  const { resetSession } = useEditSessionContext();
  const { isDirty } = useFormState();

  const hasPermissionToManageInputDataDescription =
    useHasPermissionForDescription();
  const hasPermissionToManageInputDataGroup = useHasPermissionForGroup();

  const items: ItemType[] = useMemo(() => {
    const descriptionId = description?.id ?? 0;
    const groupId = data?.dataObjectGroupId;
    const params = {
      code: description?.code,
      copy: true,
      descriptionId,
      groupId,
      type: 'description',
      valueDate: data?.valueDate,
      dataStatus: version,
      subject: data?.dataSubjectCode,
      subjectId: data?.dataSubjectId,
      versionId: selectedVersion?.id,
    };
    const disabled = !(
      description &&
      selectedVersion &&
      data?.valueDate &&
      version &&
      data?.dataSubjectCode &&
      data?.dataSubjectId
    );
    const options: ItemType[] = [];

    // Reset the edit session after a successful action, as the drafts do not contain the result of the action.
    const handleActionSuccess = async (
      response: InputUpdateResponseType | void,
    ) => {
      await resetSession();
      await onSuccess(response && { ...response, rows: null });
    };

    if (
      hasPermissionToManageInputDataDescription(
        PermissionCode.INPUT_DATA_MANAGE,
        descriptionId,
      )
    ) {
      const makeDescriptionAction =
        (copy: boolean, confirm?: boolean) => () => {
          onLoad();
          mutateAsync({
            data: { ...params, copy } as InputDataCopy,
            headers: confirmationHeaders(confirm),
          })
            .then(handleActionSuccess)
            .catch((apiErrorResponse) => {
              if (
                !confirm &&
                confirmInputDataActionWarnings(
                  apiErrorResponse,
                  makeDescriptionAction,
                  copy,
                  onClear,
                )
              ) {
                return;
              }
              onError(apiErrorResponse);
            });
        };
      options.push(
        createOption(
          {
            action: makeDescriptionAction,
            actionType: 'impute',
            sampleType: 'description',
          },
          isDirty,
          disabled,
        ),
        createOption(
          {
            action: makeDescriptionAction,
            actionType: 'copy',
            sampleType: 'description',
          },
          isDirty,
          disabled,
        ),
      );
    }

    if (
      version === 'active' &&
      groupId &&
      hasPermissionToManageInputDataGroup(
        PermissionCode.INPUT_DATA_MANAGE,
        groupId,
      )
    ) {
      const makeAction = (copy: boolean, confirm?: boolean) => () => {
        onLoad();
        mutateAsync({
          data: { ...params, type: 'group', copy } as InputDataCopy,
          headers: confirmationHeaders(confirm),
        })
          .then(handleActionSuccess)
          .catch((apiErrorResponse) => {
            if (
              !confirm &&
              confirmInputDataActionWarnings(
                apiErrorResponse,
                makeAction,
                copy,
                onClear,
              )
            ) {
              return;
            }
            onError(apiErrorResponse);
          });
      };
      options.push(
        createOption(
          {
            action: makeAction,
            actionType: 'impute',
            sampleType: 'group',
          },
          isDirty,
          disabled,
        ),
        createOption(
          { action: makeAction, actionType: 'copy', sampleType: 'group' },
          isDirty,
          disabled,
        ),
      );
    }

    return options;
  }, [
    description,
    data?.dataObjectGroupId,
    data?.valueDate,
    data?.dataSubjectCode,
    data?.dataSubjectId,
    version,
    selectedVersion,
    hasPermissionToManageInputDataDescription,
    hasPermissionToManageInputDataGroup,
    isDirty,
    onLoad,
    mutateAsync,
    onSuccess,
    onError,
    onClear,
    resetSession,
  ]);

  return (
    <Dropdown
      disabled={disabled || isLoading}
      menu={{ items }}
      trigger={['click']}
    >
      <Button
        id="input-data-actions-dropdown"
        type="text"
        shape="circle"
        icon={<MoreVertical />}
      />
    </Dropdown>
  );
}
